import { Controller } from "@hotwired/stimulus"

// Race duration: presets, or any number of minutes.
//
// The combobox owns the value — one hidden input, whichever mode is showing —
// so a host binds to that input and never has to know which half of this
// control the organizer used.
export default class extends Controller {
  static targets = ["presets", "customRow", "custom", "input"]
  static values = { custom: { type: Boolean, default: false } }

  useCustom(event) {
    event?.preventDefault()
    // The panel that held the trigger belongs to the combobox; closing it is
    // its business, so say what happened rather than reaching into it.
    document.dispatchEvent(
      new CustomEvent("combobox:opened", { detail: { source: this } }),
    )
    this._mode(true)
    this.customTarget.focus()
    this.customTarget.select()
  }

  // Leaving custom mode has to leave a *preset* selected. Without this the
  // hidden input could hold 137 while the preset trigger read "2 h" — the
  // control would be showing a value it isn't holding. Snaps to the nearest
  // preset rather than a fixed default, so "back to presets" keeps as much of
  // what was typed as the presets can express.
  usePresets(event) {
    event?.preventDefault()
    this._snapToPreset()
    this._mode(false)
  }

  _snapToPreset() {
    if (!this.hasInputTarget) return
    const options = [
      ...this.presetsTarget.querySelectorAll("[data-combobox-option-value]"),
    ]
    if (!options.length) return

    const current = parseInt(this.inputTarget.value, 10)
    if (
      options.some(
        (o) => parseInt(o.dataset.comboboxOptionValue, 10) === current,
      )
    )
      return

    const nearest = options.reduce((best, option) => {
      const value = parseInt(option.dataset.comboboxOptionValue, 10)
      const bestValue = parseInt(best.dataset.comboboxOptionValue, 10)
      return Math.abs(value - current) < Math.abs(bestValue - current)
        ? option
        : best
    })
    // Click rather than write the input: the combobox owns the trigger's face
    // and the `change` that commits, and neither should be reimplemented here.
    nearest.click()
  }

  writeCustom() {
    const minutes = parseInt(this.customTarget.value, 10)
    if (!Number.isFinite(minutes) || minutes < 1) return
    this.inputTarget.value = minutes
    this.inputTarget.dispatchEvent(new Event("change", { bubbles: true }))
  }

  customKey(event) {
    if (event.key === "Enter") {
      event.preventDefault()
      this.customTarget.blur()
    } else if (event.key === "Escape") {
      event.preventDefault()
      this.usePresets()
    }
  }

  _mode(custom) {
    this.customValue = custom
    this.presetsTarget.classList.toggle("hidden", custom)
    this.customRowTarget.classList.toggle("hidden", !custom)
  }
}
